import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCw } from "lucide-react";
import { formatMsk } from "../../utils/timeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLUMNS = [
  ["created_at", "Дата (MSK)"], ["status", "Статус"], ["name", "Имя"], ["contact", "Контакт"], ["task_type", "Тип"], ["page", "Страница"],
  ["description", "Описание"], ["privacy_version", "Версия согласия"],
];
// П.10 TZ-6 (минимальная версия): статус как фильтр + ручной переключатель, цветовая метка
export const LEAD_STATUS = {
  new: { label: "Новая", bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  in_work: { label: "В работе", bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" },
  closed: { label: "Закрытая", bg: "#F1F5F9", color: "#475569", border: "#CBD5E1" },
};

const rowOf = (l, types) => ({
  created_at: formatMsk(l.created_at), status: (LEAD_STATUS[l.status] || LEAD_STATUS.new).label, name: l.name, contact: l.contact,
  task_type: types[l.task_type] || l.task_type, page: l.page || "", description: l.description || "", privacy_version: l.consent?.privacy_version || "",
});

const downloadCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = "\ufeff" + [COLUMNS.map(([, h]) => esc(h)).join(";"), ...rows.map((r) => COLUMNS.map(([k]) => esc(r[k])).join(";"))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `zayavki_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const StatusSelect = ({ lead, onChange }) => {
  const st = LEAD_STATUS[lead.status] || LEAD_STATUS.new;
  return (
    <select data-testid={`lead-status-select-${lead.id}`} value={lead.status || "new"} onChange={(e) => onChange(lead.id, e.target.value)}
      className="text-xs font-semibold rounded-md px-2 py-1" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
      {Object.entries(LEAD_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
};

// T4-4: заявки с лид-форм (главная, лендинги) — чтобы они не жили только в ящике promo@
export const LeadsAdminTab = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const q = f ? `&status=${f}` : "";
      const [r, m] = await Promise.all([axios.get(`${API}/admin/leads?limit=1000${q}`), axios.get(`${API}/public/leads/meta`)]);
      setItems(r.data.items || []); setTotal(r.data.total || 0); setCounts(r.data.counts || {}); setTypes(m.data.task_types || {});
    } catch (e) {
      showToast?.(e.response?.data?.detail || "Не удалось загрузить заявки", "error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status });
      showToast?.(`Статус заявки: ${LEAD_STATUS[status].label}`, "success");
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || "Не удалось изменить статус", "error"); }
  };

  const rows = items.map((l) => rowOf(l, types));
  const filters = [["", `Все (${total})`], ...Object.entries(LEAD_STATUS).map(([k, v]) => [k, `${v.label} (${counts[k] || 0})`])];
  return (
    <div data-testid="admin-leads-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="text-lg font-semibold mr-auto" style={{ color: "var(--rv-navy)" }}>Заявки с сайта <span className="text-sm font-normal" style={{ color: "var(--rv-text-muted)" }} data-testid="admin-leads-total">всего {total}</span></h3>
        <button onClick={() => load()} className="reversia-btn reversia-btn-md flex items-center gap-1.5" style={{ border: "1px solid var(--rv-border)" }} data-testid="admin-leads-refresh-btn"><RefreshCw size={14} /> Обновить</button>
        <button onClick={() => downloadCsv(rows)} disabled={!rows.length} className="reversia-btn reversia-btn-teal reversia-btn-md flex items-center gap-1.5" data-testid="csv-export-leads"><Download size={14} /> CSV</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map(([k, label]) => (
          <button key={k || "all"} data-testid={`leads-filter-${k || "all"}`} onClick={() => { setFilter(k); load(k); }} className="px-3 py-1.5 rounded-md text-sm"
            style={filter === k ? { background: "var(--rv-teal)", color: "white", border: "1px solid var(--rv-teal)" } : { background: "white", color: "var(--rv-text-primary)", border: "1px solid var(--rv-border)" }}>
            {label}
          </button>
        ))}
      </div>
      {loading ? <p className="text-sm" style={{ color: "var(--rv-text-muted)" }}>Загрузка...</p> : !rows.length ? (
        <p className="text-sm" style={{ color: "var(--rv-text-muted)" }} data-testid="admin-leads-empty">Заявок нет.</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl" style={{ border: "1px solid var(--rv-border)" }}>
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead><tr style={{ background: "#F8FAFC" }}>{COLUMNS.map(([k, h]) => <th key={k} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={items[i].id} className="border-t" style={{ borderColor: "var(--rv-border)" }} data-testid={`admin-lead-row-${items[i].id}`}>
                  {COLUMNS.map(([k]) => (
                    <td key={k} className={`px-3 py-2 align-top ${k === "description" ? "min-w-[240px]" : "whitespace-nowrap"}`}>
                      {k === "status" ? <StatusSelect lead={items[i]} onChange={changeStatus} /> : (r[k] || "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
